import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { AccordionGroupComponent, AccordionItemComponent } from '@gallaghercp/core-components/accordion';
import {
    AppState,
    AuthService,
    Block,
    BlockAbstractComponent,
    PageModule,
    ParserService,
    UtilsModule,
    WindowService,
} from '@gallaghercp/core-frontend';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'core-block-accordion',
    templateUrl: './block-accordion.component.html',
    standalone: true,
    imports: [
        AccordionItemComponent,
        AccordionGroupComponent,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        UtilsModule,
        MatExpansionPanelTitle,
        PageModule,
    ],
})
export class BlockAccordionComponent extends BlockAbstractComponent implements OnInit, AfterViewInit, OnDestroy {
    public panelStates!: boolean[];
    @ViewChildren(MatExpansionPanel, { read: ElementRef }) panels!: QueryList<ElementRef>;
    subscription: Subscription = new Subscription();
    private fragment: string | null = null;

    useCoreComponentsAccordion: boolean = !!this.configService.get('useCoreComponentsAccordion');

    get accordionTheme() {
        return (this.configService.get('theme') as any)?.accordion ?? (this.config?.theme as any)?.accordion;
    }

    constructor(
        protected route: ActivatedRoute,
        protected override parser: ParserService,
        protected override auth: AuthService,
        protected store: Store<AppState>,
        protected windowService: WindowService,
        private cdr: ChangeDetectorRef,
    ) {
        super(parser, auth);
    }

    override ngOnInit() {
        super.ngOnInit();

        this.panelStates = new Array((this.block.content ?? []).length).fill(false);

        if (this.block.data?.autoExpand && this.panelStates.length > 0) {
            this.panelStates[0] = true;
        }

        this.block.content = (this.block.content ?? []).map((block: Block) => {
            if (!block.data.anchorId) {
                block.data.anchorId = (block.data.title ?? '')
                    .toLowerCase()
                    .replace(/\s+/g, '-') // Replace spaces with -
                    .replace(/[^a-z0-9-]/g, '') // Remove all non-word chars except hyphens
                    .replace(/-+/g, '-'); // Replace multiple - with single -
            }
            return block;
        });
    }

    ngAfterViewInit() {
        this.subscription.add(
            this.route.fragment.subscribe(fragment => {
                this.fragment = fragment;

                // Handle fragment matching after fragment is set and view is initialized
                if (this.fragment) {
                    setTimeout(() => {
                        this.block.content?.forEach((block: Block, index) => {
                            if (block.data.anchorId === this.fragment) {
                                this.setState(index, true);
                                this.cdr.detectChanges();
                            }
                        });
                    }, 0);
                }
            }),
        );
    }

    setState(i: number, state: boolean): void {
        this.panelStates[i] = state;
    }

    onPanelOpened(index: number): void {
        this.store
            .select((state: AppState): boolean => !!state.app['accordionPanelTransition'])
            .pipe(take(1))
            .subscribe(hasTransition => {
                if (hasTransition) {
                    const panelElement = this.panels.toArray()[index];
                    if (panelElement) {
                        setTimeout(() => {
                            const win = this.windowService.nativeWindow;

                            if (win) {
                                const elementPosition =
                                    (panelElement.nativeElement?.getBoundingClientRect().top ?? 0) + (win.scrollY ?? 0);
                                const offsetPosition = elementPosition - 100; // Subtract the offset

                                win.scrollTo({
                                    top: offsetPosition,
                                    behavior: 'smooth',
                                });
                            }
                        }, 250);
                    }
                }

                this.setState(index, true);
            });
    }

    getPanelState(i: number): string {
        return this.panelStates[i] ? 'expand' : 'collapse';
    }

    override ngOnDestroy(): void {
        this.subscription.unsubscribe();
        super.ngOnDestroy();
    }
}
